import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api-services/api.service';
import { LangService } from 'src/app/services/lang.service';
import { SharedService } from 'src/app/services/shared.service';
import {
  selectOrgDetails,
  selectUserSuccess,
} from 'src/app/store/auth/auth.selector';
import { updateCartAll } from 'src/app/store/cart/cart.actions';
import {
  selectCartCount,
  selectCartDetails,
  selectCartStatus,
} from 'src/app/store/cart/cart.selectors';
import { detectDevice } from 'src/app/store/device/device.actions';
import { selectIsMobile } from 'src/app/store/device/device.selectors';
import { AutoUnsubscribeComponent } from 'src/app/utility/auto-unsubscribe.component';
import { enabledModuleCounts, showError } from 'src/app/utility/common-logic';
import {
  isOrgLogoReady,
  shouldWaitForOrgLogoApi,
} from 'src/app/utility/logo-loading-logic';
import { LanguageMode } from 'src/app/utility/enum';
import Breadcrumb from 'src/app/utility/model/breadcrumb';
import { Cart } from 'src/app/utility/model/cart';
import { Organization } from 'src/app/utility/model/organization';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-header',
  templateUrl: environment?.components?.header?.templateUrl,
  styleUrls: environment?.components?.header?.styleUrls,
})
export class HeaderComponent
  extends AutoUnsubscribeComponent
  implements OnInit
{
  orgDetails: Organization;
  languageAvailable = environment.settings.languageAvailable;
  imgUrl = environment.url.img;
  @Input() sticky = '';
  LanguageMode = LanguageMode;
  @Output() toggleDrawer = new EventEmitter<'left' | 'right'>();
  cartDetails: any;
  cartItems: Cart[];
  cartCount: number;
  cartStatus: string;
  header = environment.settings.header;
  applyBgColorFromDjango = environment.settings.applyBgColorFromDjango;
  @Input() segments: Breadcrumb[] = [];
  showBreadCrumb = environment.settings.showBreadCrumb;
  headerLogoMaxHeight = environment.settings.headerLogoMaxHeight;
  modules = environment.settings.modules;
  topMenu = environment.settings.topMenu;
  feeds = environment.settings.feeds;
  leftNavigate = environment.settings.leftNavigate;
  showProfile = environment.settings.header.showProfile;
  showSignOut = environment.settings.header.showSignOut;
  userInitialized = false;
  mobileMenu = environment.settings.mobileMenu;
  isHideLocalLogo: boolean = false;
  isMobile: boolean = false;

  constructor(
    private router: Router,
    private api: ApiService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private store: Store,
    public langS: LangService,
    private sharedService: SharedService,
  ) {
    super();
  }

  ngOnInit() {
    this.store
      .pipe(select(selectOrgDetails), takeUntil(this.destroy$))
      .subscribe((orgDetails) => {
        this.orgDetails = orgDetails;
        if (!orgDetails?.logo) {
          this.isHideLocalLogo = true;
        } else {
          this.isHideLocalLogo = false;
        }
      });

    this.store
      .pipe(select(selectUserSuccess), takeUntil(this.destroy$))
      .subscribe((userInitialized) => {
        this.userInitialized = userInitialized;
      });

    if (this.modules.cart) {
      this.store
        .pipe(select(selectCartCount), takeUntil(this.destroy$))
        .subscribe((count) => {
          this.cartCount = count;
        });

      this.store
        .pipe(select(selectCartDetails), takeUntil(this.destroy$))
        .subscribe((res) => {
          this.cartDetails = res;
        });

      this.store
        .pipe(select(selectCartStatus), takeUntil(this.destroy$))
        .subscribe((status) => {
          this.cartStatus = status;
        });
      // This should be only to Cart Details API, everywhere else subscribe to the data
      this.getCartDetails();
    }
    this.store.dispatch(detectDevice());
    this.store
      .pipe(select(selectIsMobile), takeUntil(this.destroy$))
      .subscribe((isMobile) => {
        this.isMobile = isMobile;
      });
  }

  get enabledModuleCounts() {
    return enabledModuleCounts(this.modules);
  }

  get isLoginTnc() {
    return this.router.url === '/login-tnc';
  }

  get addHamburgerMenu() {
    return (
      this.router.url === '/home' ||
      this.router.url === '/' + this.feeds.path + '/appreciation' ||
      this.router.url === '/' + this.feeds.path + '/post-n-polls' ||
      this.router.url === '/rewards' ||
      this.router.url === '/rewards/browse-all' ||
      this.router.url === '/rewards/my-wallet' ||
      this.router.url === '/calendar' ||
      this.router.url === '/calendar/all' ||
      this.router.url === '/calendar/announcement' ||
      this.router.url === '/calendar/event' ||
      this.router.url === '/calendar/greeting' ||
      this.router.url === '/leaderboard' ||
      this.router.url === '/leaderboard/recognition' ||
      this.router.url === '/receipt-list' ||
      this.router.url === '/receipt-list/all' ||
      this.router.url === '/receipt-list/processing' ||
      this.router.url === '/receipt-list/approved' ||
      this.router.url === '/receipt-list/rejected' ||
      this.router.url === '/sales-receipt-list/all' ||
      this.router.url === '/sales-receipt-list/processing' ||
      this.router.url === '/sales-receipt-list/approved' ||
      this.router.url === '/sales-receipt-list/rejected'
    );
  }

  get bgClass() {
    return this.applyBgColorFromDjango ? 'dynamic' : '';
  }

  get stickyClass() {
    return this.sticky ? 'sticky' : '';
  }

  get hasPrimaryLogoSlot(): boolean {
    return !!(
      this.header.dynamicLogo ||
      this.header.firstLogo ||
      this.header.secondLogo
    );
  }

  get isLogoReady(): boolean {
    return isOrgLogoReady(this.userInitialized);
  }

  get isLogoLoading(): boolean {
    return (
      shouldWaitForOrgLogoApi() && !this.isLogoReady && this.hasPrimaryLogoSlot
    );
  }

  get logo2() {
    if (!this.orgDetails?.logo || this.orgDetails?.logo === this.imgUrl + '/') {
      return '';
    } else {
      return this.orgDetails?.logo.startsWith(this.imgUrl)
        ? this.orgDetails?.logo
        : this.imgUrl + this.orgDetails?.logo;
    }
  }

  get logoStyle() {
    return { 'max-height': this.headerLogoMaxHeight };
  }

  get showAppBackButton() {
    return this.orgDetails?.slug === 'toa-paints-api' ? true : false;
  }

  async getCartDetails() {
    await this.api
      .getCartDetails()
      .then((res) => {
        if (res) {
          this.cartDetails = res;
          this.store.dispatch(
            updateCartAll({
              cartDetails: this.cartDetails,
              cartCount: res?.count,
              cartStatus: res?.status,
              cartItems: res?.results,
            }),
          );
          if (res?.status === 'In process') {
            setTimeout(() => {
              this.getCartDetails();
            }, 4000);
          }
        }
      })
      .catch((err) => showError(err, this.toastr, this.translate));
  }

  backTopApp() {
    if (this.isMobile) {
      window.location.href = 'toapainter://rdsdms.com';
    }
  }

  async signOut() {
    await this.sharedService.signOut();
  }
}
